import { useEffect, useRef, useState } from "react";

export type PresenceEntry<T> = {
  /** Stable key for React reconciliation. */
  key: string;
  /** The latest item data while present; the last-seen data while exiting. */
  item: T;
  /** True once the item has left the incoming set and is animating out. */
  exiting: boolean;
};

function reconcile<T>(
  prev: readonly PresenceEntry<T>[],
  items: readonly T[],
  getKey: (item: T) => string,
): PresenceEntry<T>[] {
  const incoming = new Map(items.map((it) => [getKey(it), it] as const));
  const seen = new Set<string>();
  const next: PresenceEntry<T>[] = [];

  for (const entry of prev) {
    seen.add(entry.key);
    const fresh = incoming.get(entry.key);
    if (fresh !== undefined) {
      // Still present (or returned mid-exit): refresh the data. An entry
      // that did not change keeps its object.
      next.push(
        fresh === entry.item && !entry.exiting
          ? entry
          : { key: entry.key, item: fresh, exiting: false },
      );
    } else if (entry.exiting) {
      // Already animating out — keep until its timer fires.
      next.push(entry);
    } else {
      // Just removed — begin exit, keep last-seen data.
      next.push({ ...entry, exiting: true });
    }
  }

  // Brand-new items.
  for (const [key, item] of incoming) {
    if (!seen.has(key)) next.push({ key, item, exiting: false });
  }

  return next;
}

/**
 * Tracks a keyed list across renders so removed items stay rendered (flagged
 * `exiting`) until their exit animation finishes, then drops them. Mirrors the
 * AnimatePresence pattern, but controlled so callers can drive a prop-based
 * exit animation (markers live inside Google Maps AdvancedMarker, whose portal
 * DOM is torn down on unmount — a React-unmount-driven exit can't play there).
 *
 * The list is reconciled DURING render when `items` changes (React's
 * "adjust state while rendering" pattern), not in an effect: an effect commit
 * rendered every marker twice per data answer — once with the stale list,
 * once with the new one.
 *
 * `getKey` must be stable (module-level or memoized).
 */
export function useMarkerPresence<T>(
  items: readonly T[],
  getKey: (item: T) => string,
  exitMs: number,
): PresenceEntry<T>[] {
  const [state, setState] = useState(() => ({
    items,
    rendered: items.map((item) => ({ key: getKey(item), item, exiting: false })),
  }));
  let { rendered } = state;
  if (state.items !== items) {
    rendered = reconcile(state.rendered, items, getKey);
    setState({ items, rendered });
  }

  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Cancel the removal of anything that came back, and schedule it for
  // anything now exiting.
  useEffect(() => {
    for (const entry of rendered) {
      const timer = timers.current.get(entry.key);
      if (!entry.exiting) {
        if (timer) {
          clearTimeout(timer);
          timers.current.delete(entry.key);
        }
        continue;
      }
      if (timer) continue;
      timers.current.set(
        entry.key,
        setTimeout(() => {
          timers.current.delete(entry.key);
          setState((prev) => ({
            ...prev,
            // Only an entry still exiting leaves; one that came back stays.
            rendered: prev.rendered.filter(
              (e) => !(e.key === entry.key && e.exiting),
            ),
          }));
        }, exitMs),
      );
    }
  }, [rendered, exitMs]);

  // Clear all pending timers on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  return rendered;
}
